<template>
  <div class="space-y-6">
    <div class="bg-white rounded-xl shadow-sm border border-neutral-200 p-6">
      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 class="text-lg font-bold text-neutral-900">通知与反馈</h2>
          <p class="text-sm text-neutral-500 mt-1">
            向用户推送平台公告 / 版本更新，或给指定用户单独发消息
          </p>
        </div>
        <div class="flex gap-2 bg-neutral-100 rounded-lg p-1">
          <button
            v-for="t in tabs"
            :key="t.id"
            @click="currentTab = t.id"
            class="px-4 py-2 rounded-md text-sm font-medium transition-colors"
            :class="currentTab === t.id ? 'bg-white text-primary shadow-sm' : 'text-neutral-500 hover:text-neutral-700'"
          >
            <i :class="'fa ' + t.icon + ' mr-1'"></i>{{ t.name }}
          </button>
        </div>
      </div>

      <!-- ===== 发布通知 ===== -->
      <div v-if="currentTab === 'publish'" class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-neutral-700 mb-1">通知类型</label>
            <select
              v-model="form.type"
              class="w-full px-3 py-2.5 rounded-lg border border-neutral-300 outline-none focus:border-primary text-sm"
            >
              <option value="announce">平台公告</option>
              <option value="update">版本更新</option>
              <option value="system">系统通知</option>
            </select>
          </div>
          <div>
            <label class="block text-sm font-medium text-neutral-700 mb-1">发送范围</label>
            <div class="flex gap-2">
              <button
                @click="form.scope = 'all'"
                class="flex-1 px-3 py-2.5 rounded-lg border text-sm transition-colors"
                :class="form.scope === 'all' ? 'border-primary text-primary bg-primary/5 font-medium' : 'border-neutral-300 text-neutral-600'"
              >
                <i class="fa fa-users mr-1"></i>全体用户
              </button>
              <button
                @click="form.scope = 'users'"
                class="flex-1 px-3 py-2.5 rounded-lg border text-sm transition-colors"
                :class="form.scope === 'users' ? 'border-primary text-primary bg-primary/5 font-medium' : 'border-neutral-300 text-neutral-600'"
              >
                <i class="fa fa-user-plus mr-1"></i>指定用户
                <span v-if="form.userIds.length" class="ml-1 text-xs"
                  >（{{ form.userIds.length }}）</span
                >
              </button>
            </div>
          </div>
          <div v-if="form.scope === 'users'">
            <label class="block text-sm font-medium text-neutral-700 mb-1">
              选择接收用户
              <span class="text-xs text-neutral-400 ml-1"
                >输入姓名 / 手机号 / 账号搜索（最多 50 条）</span
              >
            </label>
            <el-select
              v-model="form.userIds"
              multiple
              filterable
              remote
              :remote-method="searchUsers"
              :loading="searching"
              :max-collapse-tags="3"
              collapse-tags
              collapse-tags-tooltip
              placeholder="搜索并选择多个用户…"
              class="w-full"
              style="width: 100%"
              no-data-text="无匹配用户"
              clearable
            >
              <el-option
                v-for="u in userOptions"
                :key="u.uid"
                :label="userLabel(u)"
                :value="u.uid"
              >
                <div class="flex items-center justify-between gap-2">
                  <span>{{ u.name }}</span>
                  <span class="text-xs text-neutral-400 truncate"
                    >{{ u.account || u.phone || u.schName || "" }}</span
                  >
                </div>
              </el-option>
            </el-select>
            <div class="mt-2 flex flex-wrap gap-1" v-if="pickedUsers.length">
              <span
                v-for="u in pickedUsers"
                :key="u.uid"
                class="text-xs bg-primary/10 text-primary px-2 py-1 rounded"
              >
                {{ u.name }}
                <i
                  class="fa fa-times ml-1 cursor-pointer hover:text-red-500"
                  @click="removePicked(u.uid)"
                ></i>
              </span>
            </div>
            <div
              v-if="!pickedUsers.length"
              class="mt-2 text-xs text-neutral-400"
            >
              <i class="fa fa-info-circle mr-1"></i
              >未选择时不可发送。可跨页搜索（接口每次返回最多 50 条）。
            </div>
          </div>
          <div>
            <label class="block text-sm font-medium text-neutral-700 mb-1">标题</label>
            <input
              v-model="form.title"
              maxlength="50"
              placeholder="如：平台新增「小义问宝」AI 助手"
              class="w-full px-3 py-2.5 rounded-lg border border-neutral-300 outline-none focus:border-primary text-sm"
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-neutral-700 mb-1">内容</label>
            <textarea
              v-model="form.content"
              rows="6"
              maxlength="1000"
              placeholder="写给用户的通知正文…"
              class="w-full px-3 py-2.5 rounded-lg border border-neutral-300 outline-none focus:border-primary text-sm resize-none"
            ></textarea>
          </div>
          <button
            @click="publish"
            :disabled="sending"
            class="w-full px-4 py-2.5 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
          >
            <i class="fa fa-paper-plane mr-1"></i>{{ sending ? "发送中…" : "立即发送" }}
          </button>
        </div>

        <!-- 预览 -->
        <div>
          <div class="text-sm font-medium text-neutral-700 mb-2">用户端预览</div>
          <div class="rounded-xl border border-neutral-200 p-4 bg-neutral-50">
            <div class="flex items-start gap-3">
              <div
                class="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                :class="previewBg"
              >
                <i :class="'fa ' + previewIcon"></i>
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2">
                  <span class="w-2 h-2 rounded-full bg-red-500"></span>
                  <span class="font-medium text-neutral-900">{{
                    form.title || "通知标题"
                  }}</span>
                </div>
                <p class="text-sm text-neutral-600 mt-1 whitespace-pre-wrap">
                  {{ form.content || "通知内容将显示在这里" }}
                </p>
                <div class="text-xs text-neutral-400 mt-2">
                  <i class="fa fa-clock-o mr-1"></i>刚刚
                </div>
              </div>
            </div>
          </div>
          <div class="mt-3 text-xs text-neutral-500">
            <i class="fa fa-info-circle mr-1"></i>
            {{
              form.scope === "all"
                ? "群发时每位用户各自收到一条，已读状态独立统计。"
                : pickedUsers.length
                ? "将发送给已选 " + pickedUsers.length + " 位用户。"
                : "请在左侧搜索并勾选要接收的用户。"
            }}
          </div>
        </div>
      </div>

      <!-- ===== 已发通知 ===== -->
      <div v-else>
        <div class="relative max-w-xs mb-4">
          <input
            v-model="searchKey"
            @input="handleSearch"
            placeholder="搜索标题/内容…"
            class="w-full px-4 py-2 pl-10 rounded-lg border border-neutral-300 focus:border-primary text-sm"
          />
          <i class="fa fa-search absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"></i>
        </div>

        <div v-loading="loading" style="min-height: 120px" class="space-y-3">
          <div
            v-for="n in list"
            :key="n.id"
            class="bg-neutral-50 rounded-xl border border-neutral-200 p-4"
          >
            <div class="flex items-start justify-between gap-3">
              <div class="min-w-0">
                <div class="flex items-center gap-2 flex-wrap">
                  <span
                    class="text-xs px-2 py-0.5 rounded-full"
                    :class="typeClass(n.type)"
                    >{{ typeText(n.type) }}</span
                  >
                  <span class="font-medium text-neutral-900">{{ n.title }}</span>
                  <span
                    class="text-xs px-2 py-0.5 rounded-full"
                    :class="n.isRead === 1 ? 'bg-neutral-100 text-neutral-500' : 'bg-blue-50 text-primary'"
                    >{{ n.isRead === 1 ? "已读" : "未读" }}</span
                  >
                </div>
                <p class="text-sm text-neutral-600 mt-1 whitespace-pre-wrap">
                  {{ n.content }}
                </p>
                <div class="text-xs text-neutral-400 mt-2">
                  <i class="fa fa-user-o mr-1"></i>接收者：{{ n.userName || n.userId }}
                  <span class="ml-3"><i class="fa fa-clock-o mr-1"></i>{{ n.createTime }}</span>
                </div>
              </div>
            </div>
          </div>
          <div v-if="!loading && list.length === 0" class="text-center py-12 text-neutral-400">
            <i class="fa fa-inbox text-4xl mb-3"></i>
            <p>暂无通知记录</p>
          </div>
        </div>

        <div v-if="total > pageSize" class="flex justify-center mt-5">
          <el-pagination
            background
            layout="prev, pager, next"
            :current-page="page"
            :page-size="pageSize"
            :total="total"
            @current-change="changePage"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ElMessage } from "element-plus";

export default {
  name: "NoticeManager",
  data() {
    return {
      currentTab: "publish",
      tabs: [
        { id: "publish", name: "发布通知", icon: "fa-paper-plane" },
        { id: "history", name: "发送记录", icon: "fa-history" },
      ],
      form: {
        type: "announce",
        scope: "all",
        userIds: [],
        title: "",
        content: "",
      },
      sending: false,
      // 远程搜索候选（最多 50 条）
      userOptions: [],
      // 已选用户（id -> 用户对象），便于显示已选标签
      pickedMap: {},
      searching: false,
      // 发送记录
      list: [],
      total: 0,
      page: 1,
      pageSize: 10,
      searchKey: "",
      loading: false,
    };
  },
  computed: {
    previewIcon() {
      if (this.form.type === "update") return "fa-rocket";
      if (this.form.type === "announce") return "fa-bullhorn";
      return "fa-info-circle";
    },
    previewBg() {
      if (this.form.type === "update") return "bg-purple-50 text-purple-600";
      if (this.form.type === "announce") return "bg-orange-50 text-orange-500";
      return "bg-blue-50 text-primary";
    },
    // 当前已选用户列表（按勾选顺序去重）
    pickedUsers() {
      const arr = [];
      const seen = new Set();
      for (const id of this.form.userIds) {
        if (!seen.has(id) && this.pickedMap[id]) {
          seen.add(id);
          arr.push(this.pickedMap[id]);
        }
      }
      return arr;
    },
  },
  watch: {
    currentTab(val) {
      if (val === "history") this.getList();
    },
    // 勾选后补齐已选用户快照，保证下方标签正常展示
    "form.userIds"(ids) {
      for (const id of ids || []) {
        if (!this.pickedMap[id]) {
          const hit = (this.userOptions || []).find((u) => u.uid === id);
          this.pickedMap[id] = hit || { uid: id, name: id };
        }
      }
    },
  },
  created() {
    // 首次进入预拉一次最近用户，方便快速选择
    this.searchUsers("");
  },
  methods: {
    userLabel(u) {
      const meta = [u.account, u.phone, u.schName].filter(Boolean).join(" · ");
      return meta ? `${u.name}（${meta}）` : u.name;
    },
    removePicked(uid) {
      this.form.userIds = this.form.userIds.filter((x) => x !== uid);
      delete this.pickedMap[uid];
    },
    async searchUsers(keyword) {
      this.searching = true;
      try {
        const res = await this.$axios({
          url: "notice/admin/user-search",
          method: "GET",
          params: { keyword: keyword || "" },
        });
        if (res.code === 200 && res.data) {
          this.userOptions = res.data.list || [];
          // 同步建立已选用户映射，用于标签展示
          for (const u of this.userOptions) {
            if (this.form.userIds.includes(u.uid)) this.pickedMap[u.uid] = u;
          }
        } else {
          this.userOptions = [];
        }
      } catch (e) {
        this.userOptions = [];
      }
      this.searching = false;
    },
    async publish() {
      if (!this.form.title.trim() || !this.form.content.trim()) {
        ElMessage({ message: "请填写标题与内容", type: "warning" });
        return;
      }
      if (this.form.scope === "users" && this.form.userIds.length === 0) {
        ElMessage({ message: "请至少选择 1 位接收用户", type: "warning" });
        return;
      }
      this.sending = true;
      try {
        const payload = {
          type: this.form.type,
          scope: this.form.scope,
          title: this.form.title,
          content: this.form.content,
        };
        if (this.form.scope === "users") {
          payload.userIds = [...this.form.userIds];
        }
        const res = await this.$axios({
          url: "notice/admin/publish",
          method: "POST",
          data: { data: await this.$aesEncrypt(payload) },
        });
        if (res.code === 200) {
          const n = (res.data && res.data.count) || 0;
          ElMessage({
            message: "已发送给 " + n + " 位用户",
            type: "success",
          });
          this.form.title = "";
          this.form.content = "";
        } else {
          ElMessage({ message: res.msg || "发送失败", type: "error" });
        }
      } catch (e) {
        ElMessage({ message: "发送失败", type: "error" });
      }
      this.sending = false;
    },
    async getList() {
      this.loading = true;
      try {
        const res = await this.$axios({
          url: "notice/admin/list",
          method: "POST",
          data: {
            data: await this.$aesEncrypt({
              page: this.page,
              pageSize: this.pageSize,
              searchKey: this.searchKey,
            }),
          },
        });
        if (res.code === 200 && res.data) {
          this.list = res.data.list || [];
          this.total = res.data.total || 0;
        }
      } catch (e) {
        console.error("获取通知列表失败:", e);
      }
      this.loading = false;
    },
    handleSearch() {
      clearTimeout(this._t);
      this._t = setTimeout(() => {
        this.page = 1;
        this.getList();
      }, 400);
    },
    changePage(p) {
      this.page = p;
      this.getList();
    },
    typeText(t) {
      const m = { announce: "平台公告", update: "版本更新", reply: "反馈回复", system: "系统通知" };
      return m[t] || "系统通知";
    },
    typeClass(t) {
      if (t === "update") return "bg-purple-50 text-purple-600";
      if (t === "reply") return "bg-green-50 text-green-600";
      if (t === "announce") return "bg-orange-50 text-orange-500";
      return "bg-blue-50 text-primary";
    },
  },
};
</script>
